import { cn, formatPrice } from '@/lib/utils';
import { Property } from '@/types/property';
import { useNavigate } from '@tanstack/react-router';
import type mapboxgl from 'mapbox-gl'; // Import type for mapboxgl
import { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import PropertiesMapPopup from './PropertiesMapPopup';

// VITE_MAPBOX_TOKEN is still needed, ensure it's in your .env file
// mapboxgl.accessToken = import.meta.env['VITE_MAPBOX_TOKEN']; // This will be set after dynamic import

interface PropertiesMapProps {
  properties: Property[];
  hoveredListing: string | null;
}

export default function PropertiesMap({ properties, hoveredListing }: PropertiesMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null); // Renamed for clarity
  const markersRef = useRef<{ [key: string]: mapboxgl.Marker }>({});
  const popupsRef = useRef<{ [key: string]: mapboxgl.Popup }>({});
  const navigate = useNavigate();
  const [mapboxglLib, setMapboxglLib] = useState<typeof mapboxgl | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const styleElementRef = useRef<HTMLStyleElement | null>(null);

  useEffect(() => {
    let isMounted = true;
    const loadMapbox = async () => {
      try {
        const mapbox = (await import('mapbox-gl')).default;
        await import('mapbox-gl/dist/mapbox-gl.css');

        if (!isMounted) return;

        mapbox.accessToken = import.meta.env['VITE_MAPBOX_TOKEN'];
        setMapboxglLib(mapbox);
      } catch (error) {
        console.error('Failed to load Mapbox GL:', error);
        if (isMounted) setIsLoading(false); // Stop loading if error
      }
    };

    loadMapbox();

    return () => {
      isMounted = false;
      // Cleanup map instance and styles when component unmounts or before re-initialization
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      Object.values(markersRef.current).forEach(marker => marker.remove());
      markersRef.current = {};
      Object.values(popupsRef.current).forEach(popup => popup.remove());
      popupsRef.current = {};
      if (styleElementRef.current) {
        document.head.removeChild(styleElementRef.current);
        styleElementRef.current = null;
      }
    };
  }, []);


  useEffect(() => {
    if (!mapboxglLib || !mapContainer.current || mapRef.current) return; // Don't initialize if already initialized

    try {
      mapRef.current = new mapboxglLib.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [-79.3832, 43.6532], // Toronto
        zoom: 10,
        failIfMajorPerformanceCaveat: true,
        attributionControl: false,
      });

      // Add custom CSS to style Mapbox popups and markers
      const style = document.createElement('style');
      style.textContent = `
        .mapboxgl-popup-content {
          padding: 0 !important;
          border-radius: 0.5rem !important;
          overflow: hidden !important;
          box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1) !important;
        }
        .mapboxgl-popup-close-button {
          display: none !important;
        }
        .mapboxgl-popup-tip {
          display: none !important;
        }
        .marker {
          will-change: transform;
          transform-origin: bottom center;
          cursor: pointer;
        }
        .marker-content {
          @apply bg-primary text-primary-foreground px-2 py-1 rounded-md font-semibold text-sm shadow
          white-space: nowrap;
        }
      `;
      document.head.appendChild(style);
      styleElementRef.current = style;

      mapRef.current.on('load', () => {
        if (mapRef.current) {
          mapRef.current.addControl(new mapboxglLib.NavigationControl());
        }
        setIsLoading(false); // Map is loaded and ready
      });

      mapRef.current.on('error', (e) => {
        console.error('Mapbox error:', e);
        setIsLoading(false);
      });

    } catch (error) {
      console.error('Error initializing map:', error);
      setIsLoading(false);
    }
  }, [mapboxglLib]);


  // Update markers when listings change and map is initialized
  useEffect(() => {
    if (!mapboxglLib || !mapRef.current || isLoading || !mapRef.current.isStyleLoaded()) return;

    try {
      // Clear existing markers and popups before adding new ones
      Object.values(markersRef.current).forEach(marker => marker.remove());
      markersRef.current = {};
      Object.values(popupsRef.current).forEach(popup => popup.remove());
      popupsRef.current = {};

      properties.forEach((property) => {
        if (!mapRef.current || !property.latitude || !property.longitude) return;

        const markerEl = document.createElement('div');
        markerEl.className = 'marker';
        markerEl.innerHTML = `<div class="marker-content">${formatPrice(property.list_price)}</div>`;

        const popupEl = document.createElement('div');
        const popupRoot = createRoot(popupEl);
        popupRoot.render(
          <PropertiesMapPopup
            property={property}
            onNavigate={() => {
              navigate({ to: `/properties/$id`, params: { id: property.property_id } });
            }}
          />
        );

        const popup = new mapboxglLib.Popup({
          offset: 25,
          closeButton: false,
          maxWidth: 'none',
          className: 'bg-transparent p-0!',
        }).setDOMContent(popupEl);

        const marker = new mapboxglLib.Marker({
          element: markerEl,
          anchor: 'bottom',
        })
          .setLngLat([property.longitude, property.latitude])
          .setPopup(popup);

        marker.addTo(mapRef.current);
        markersRef.current[property.property_id] = marker;
        popupsRef.current[property.property_id] = popup;

        markerEl.addEventListener('click', (e) => {
          e.stopPropagation(); // Prevent map click event when clicking marker
          // Close all other popups
          Object.values(popupsRef.current).forEach(p => p.remove());
          // Open this popup
          if (mapRef.current) { // Ensure map is still available
            popup.addTo(mapRef.current);
          }
        });
      });

      if (properties.length > 0) {
        const bounds = new mapboxglLib.LngLatBounds();
        properties.forEach(listing => {
          if (listing.longitude && listing.latitude) {
            bounds.extend([listing.longitude, listing.latitude]);
          }
        });
        mapRef.current.fitBounds(bounds, { padding: 100, maxZoom: 15, duration: 0 });
      }
    } catch (error) {
      console.error('Error updating markers:', error);
    }
  }, [properties, mapboxglLib, isLoading, navigate]); // Added isLoading to dependencies

  // Handle hover effects from property cards
  useEffect(() => {
    if (!mapRef.current || !mapboxglLib || isLoading) return;

    // Close all popups first, unless one is explicitly hovered
    Object.entries(popupsRef.current).forEach(([id, popup]) => {
      if (id !== hoveredListing) {
        popup.remove();
      }
    });
    
    // Remove highlights from all markers
    Object.values(markersRef.current).forEach(marker => {
        marker.getElement().querySelector('.marker-content')?.classList.remove('ring-2', 'ring-primary-dark', 'ring-offset-2', 'scale-110');
        marker.getElement().style.zIndex = '';
    });

    if (hoveredListing && popupsRef.current[hoveredListing] && markersRef.current[hoveredListing]) {
      const popup = popupsRef.current[hoveredListing];
      const marker = markersRef.current[hoveredListing];
      
      if (!popup.isOpen() && mapRef.current) {
        popup.addTo(mapRef.current);
      }
      
      const markerEl = marker.getElement();
      if (markerEl) {
        markerEl.querySelector('.marker-content')?.classList.add('ring-2', 'ring-primary-dark', 'ring-offset-2', 'scale-110');
        markerEl.style.zIndex = '10'; // Bring hovered marker to front
      }
      // Pan to marker if it's outside current view
      if (mapRef.current && mapRef.current.getBounds() && !mapRef.current.getBounds()?.contains(marker.getLngLat())) {
        mapRef.current.panTo(marker.getLngLat(), { duration: 300 });
      }

    }
  }, [hoveredListing, mapboxglLib, isLoading]); // Added isLoading

  return (
    <div ref={mapContainer} className={cn("relative w-full h-full rounded-lg overflow-hidden shadow-lg")}>
      {isLoading && (
        <div className="absolute inset-0 bg-muted flex items-center justify-center z-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mr-2"></div>
          <div className="text-muted-foreground">Loading map...</div>
        </div>
      )}
    </div>
  );
}
